import Checkbox from 'components/Form/Checkbox';
import CheckboxRadioGroup from 'components/Form/CheckboxRadioGroup';
import Field from 'components/Form/Field';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import InputSanitized from 'components/Form/InputSanitized';
import {sanitizeTaxRate} from 'components/Form/InputSanitized/utils';
import Layout from 'components/Layout';
import Link from 'components/Link';
import {trimZeroes, UNSELECTED} from 'components/ModalTaxCombination/utils';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import useBeforeUnload from 'hooks/useBeforeUnload';
import lodash from 'lodash';
import {FC, FocusEvent, useCallback, useMemo} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';

import {FormFields, Request, usePutTaxCombinationsEdit} from '../services';
import {PartnerTaxSettingsEditBackendData} from '../types';
import SortableSection from './SortableSection';
import {taxSettings} from './styles.module.css';
import getInitialFormField from './utils';

const PartnerTaxSettingsEditPage: FC = () => {
    const {data} = useBackend<PartnerTaxSettingsEditBackendData>();

    const initialData = useMemo(() => getInitialFormField(data), [data]);
    const form = useForm<FormFields>({defaultValues: initialData});

    const {isLoading, mutateAsync} = usePutTaxCombinationsEdit();
    const formData = form.watch();

    const {allowPageChange} = useBeforeUnload(
        !lodash.isEqual(initialData, formData),
    );

    const onTaxRateBlur = useCallback(
        (name: 'alternateTaxRate' | 'defaultTaxRate') =>
            (event: FocusEvent<HTMLInputElement>) => {
                const {value} = event.target;

                if (value === '' || value === UNSELECTED) {
                    return;
                }

                const taxRate = trimZeroes(value);
                form.setValue(name, taxRate);
            },
        [form],
    );

    const onSubmit = () => {
        const request: Request = {
            alternateTaxRate: formData.alternateTaxRate || null,
            defaultTaxRate: formData.defaultTaxRate,
            enableJpWithholdingTax: formData.enableJpWithholdingTax,
            taxCombinations: formData.taxCombinations.map((item, position) => ({
                isTaxInclusive: item.isTaxInclusive,
                mid: item.id.startsWith('new_') ? undefined : item.id,
                position,
                taxRate: item.taxRate,
                taxType: item.taxType,
            })),
        };

        mutateAsync(request)
            .then(() => {
                allowPageChange();
                setUrl(window.Urls.partner_item_view());
            })
            .catch(onApiFormError(form));
    };

    const link = (
        <Link
            href="https://help.makeleaps.jp/ja/articles/3560324-%E8%87%AA%E7%A4%BE%E6%83%85%E5%A0%B1%E3%82%92%E7%B7%A8%E9%9B%86%E3%81%99%E3%82%8B#h_fc42bcc5a8"
            isOpenInNewTab={true}
            key={0}
            rel="noreferrer"
        >
            {t`Help Center`}
        </Link>
    );

    return (
        <div className={taxSettings}>
            <FormProvider {...form}>
                <FormResult className="mb-4" />
                <NavigationTabs.Partner selection="settings" />
                <FormHeader
                    button={{
                        isLoading,
                        kind: 'primary',
                        label: t`Save`,
                        onClick: form.handleSubmit(onSubmit),
                        type: 'submit',
                    }}
                    title={t`Tax-related Settings`}
                />
                <form className="flex flex-col gap-6">
                    <div className="flex flex-col gap-3">
                        <SectionHeader
                            hideHR={true}
                            level={2}
                            subtitle={t`Appear as default choices when changing a line item's Tax Settings`}
                            title={t`Favorite Tax Settings`}
                        />
                        <SortableSection />
                    </div>
                    <div className="flex flex-col gap-3">
                        <SectionHeader
                            hideHR={true}
                            level={2}
                            title={t`Other`}
                        />
                        <Layout.SideBySide className="max-w-xl">
                            <Field
                                label={
                                    <div className="max-w-36">{t`Enable Japanese Withholding Tax`}</div>
                                }
                                type="checkbox"
                            >
                                <CheckboxRadioGroup>
                                    <Checkbox
                                        className="my-1"
                                        name="enableJpWithholdingTax"
                                    />
                                </CheckboxRadioGroup>
                            </Field>
                            <InputSanitized
                                helpText={jt`Click to see ${link} for how this setting gets applied in MakeLeaps`}
                                label={
                                    <div className="flex flex-col">
                                        <span>{t`Default Tax Rate`}</span>
                                        <span>{t`(Legacy)`}</span>
                                    </div>
                                }
                                name="defaultTaxRate"
                                onBlurCapture={onTaxRateBlur('defaultTaxRate')}
                                required={true}
                                requiredType="hidden"
                                sanitizer={sanitizeTaxRate}
                                suffix={<div className="py-2">%</div>}
                            />
                            <InputSanitized
                                classNameInput=""
                                helpText={jt`Click to see ${link} for how this setting gets applied in MakeLeaps`}
                                label={
                                    <div className="flex flex-col">
                                        <span>{t`Alternate Tax Rate`}</span>
                                        <span>{t`(Legacy)`}</span>
                                    </div>
                                }
                                name="alternateTaxRate"
                                onBlurCapture={onTaxRateBlur(
                                    'alternateTaxRate',
                                )}
                                sanitizer={sanitizeTaxRate}
                                suffix={<div className="py-2">%</div>}
                            />
                        </Layout.SideBySide>
                    </div>
                </form>
            </FormProvider>
        </div>
    );
};

export default PartnerTaxSettingsEditPage;
